import type { DiagnosticAction, DiagnosticEvent } from './diagnostic-types'

type DiagnosticStoreOptions = {
  maxActions?: number
  maxEvents?: number
}

type ActionContext = Pick<DiagnosticEvent, 'action' | 'correlation'>

function getEventSignature(event: DiagnosticEvent): string {
  if (event.request) {
    return [event.kind, event.request.client, event.request.method, event.request.path, event.request.status, event.error.name, event.error.message].join('|')
  }
  return [event.kind, event.pageUrl, event.error.name, event.error.message].join('|')
}

export function createDiagnosticStore({ maxActions = 30, maxEvents = 20 }: DiagnosticStoreOptions = {}) {
  let actions: DiagnosticAction[] = []
  let events: DiagnosticEvent[] = []
  const listeners = new Set<() => void>()

  const emit = () => listeners.forEach((listener) => listener())

  return {
    clear() {
      actions = []
      events = []
      emit()
    },
    getActionContext(at: number): ActionContext {
      const action = actions.find((candidate) => candidate.at <= at && at - candidate.at <= 2_000)
      return action ? { action, correlation: 'recent-action' } : { correlation: 'background' }
    },
    getEvents() {
      return events
    },
    getActionsBefore(at: number, limit = 10) {
      return actions.filter((action) => action.at <= at).slice(0, limit)
    },
    recordAction(action: DiagnosticAction) {
      actions = [action, ...actions].slice(0, maxActions)
    },
    recordEvent(event: DiagnosticEvent) {
      const previous = events[0]
      if (previous && event.at - previous.at <= 30_000 && getEventSignature(previous) === getEventSignature(event)) {
        events = [{ ...event, occurrences: (previous.occurrences ?? 1) + 1 }, ...events.slice(1)]
        emit()
        return
      }
      events = [event, ...events].slice(0, maxEvents)
      emit()
    },
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

export const diagnosticsStore = createDiagnosticStore()
